import type { QueryClient } from '@tanstack/react-query';

/**
 * localStorage key used by the async-storage persister configured in `router.tsx`.
 * Kept in sync with the explicit `key` passed to `createAsyncStoragePersister`.
 */
export const PERSISTED_QUERY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';

/**
 * Drop all cached query data from BOTH the in-memory client and the persisted
 * localStorage blob.
 *
 * `queryClient.clear()` only updates memory; the persister writes to localStorage
 * on a ~1s throttle, so we remove the key directly to guarantee a stale copy isn't
 * restored on the next load. Call this on auth transitions (login / logout / NGO
 * switch) so a session never inherits the previous state's cache — otherwise a
 * persisted `ngo.*` query result cached under the previous session could be restored
 * and read synchronously by the next NGO/user, showing stale (or someone else's) data
 * before the cookie-backed refetch can land.
 */
export function clearPersistedQueryCache(queryClient: QueryClient): void {
	queryClient.clear();
	if (typeof window !== 'undefined') {
		window.localStorage.removeItem(PERSISTED_QUERY_CACHE_KEY);
	}
}

/**
 * Run `restore` only once THIS document's own dehydrated payload has been applied to the query cache.
 *
 * WHY THE TIMING IS THE WHOLE FIX (TEC-3758). The persisted cache and the SSR payload are two sources for
 * the same queries, and react-query already resolves that correctly: `hydrate` overwrites an entry only
 * when the incoming data is NEWER, so a snapshot from a previous visit can never clobber the figures this
 * request just rendered. What it cannot protect is the FIRST client render. Restoring while the router was
 * built put the previous visit's figures in the cache before that render, so `useSuspenseQuery` resolved
 * from them instead of suspending on the streamed promise, the render disagreed with the text the server
 * had already written, and React reported `#418 args[]=text` and regenerated the campaign card subtree.
 * The payload then won, correctly, but a frame too late.
 *
 * Deferring closes that window without giving up the cache: afterwards every query the document carried
 * already holds something newer, so the restore is a no-op for them, while queries the document did NOT
 * carry — a campaign detail page seen last week, a reload on a flaky connection — are still served from
 * localStorage, which is the case the cache exists for.
 *
 * `DOMContentLoaded` is the signal because the payload travels in inline `<script>` tags at the end of
 * `<body>`: inline scripts block the parser, so by the time it fires they have all executed. A caller
 * arriving after parsing (a client-side navigation building a fresh router) runs immediately.
 *
 * Restoring is deliberately NOT split from the save subscription. `persistQueryClient` restores and only
 * then subscribes, and that order matters: a save triggered before the restore would dehydrate the current
 * document over the blob and destroy the very entries we were about to read.
 */
export function afterSsrPayloadApplied(restore: () => void): void {
	if (typeof document === 'undefined') return;

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => restore(), { once: true });
		return;
	}

	restore();
}
